import { frontloadConnect } from 'react-frontload';
import { connect } from 'react-redux';
import {
  generateBreadcrumbSchema as _generateBreadcrumbSchema,
  generateProductSchema as _generateProductSchema,
} from '@nykaa/jsonld-shared/store/action';
import { getSchemaData } from '@nykaa/jsonld-shared/helper';
import redirectIncorrectSlug from '@nykaa/seo/utils/redirectIncorrectSlug';
import { isValidObject } from '@nykaa/utils/validations';
import {
  fetchProduct as _fetchProduct,
  trackProductJourney,
  fetchBanners as _fetchBanners,
  productUnMount,
} from 'shared/store/product/action';
import {
  selectCurrentSku,
  selectIsProductLoading,
  selectBrandName,
  selectIsFetchingError,
  selectProductMedia,
  selectProductRelatedVideo,
  getSelectedSku,
} from 'shared/store/product/selector';
import View from 'shared/layouts/Product';
import { State } from 'store/types';
import { isEligible } from 'store/app/selector';
import { fetchBreadCrumbs as _fetchBreadCrumbs } from 'store/app/actions';
import { getCatgoryLevelId } from 'helpers/utils/product';
import { SNS_PD_PAGE_PREFIX } from 'constants/urls';
import { isEligibleForDifferentialPricing } from 'hooks/priceReveal/differentialPrice';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { AB_NYKAA_NOW_RAAP } from 'constants/experiments';
import { QC_INTERNAL_SLUG } from 'constants/productPage';

const mapStateToProps = (state: State) => {
  const { productPage, remoteConfig, appReducer, auth } = state;
  return {
    currentSku: selectCurrentSku(state),
    loading: selectIsProductLoading(state),
    fetchingError: selectIsFetchingError(state),
    brandName: selectBrandName(state),
    primaryCategories: productPage.product?.primaryCategories,
    parentId: productPage.product?.id,
    pdPageBanners: remoteConfig.switches.pdPageBanners,
    hasUniversalBanners: !!remoteConfig.switches.universalBanners,
    variantType: productPage.product?.variantType,
    displayCombo: productPage.product?.displayCombo,
    comboProductEnabled: isEligible(
      remoteConfig.configs?.AB?.comboProducts,
      appReducer.run
    ),
    personalCouponsEnabled: isEligible(
      remoteConfig.configs?.AB?.personalCoupons,
      appReducer.run
    ),
    defaultPid: productPage.product?.defaultPid,
    media: selectProductMedia(state),
    pdpCustomVideoEnabled: remoteConfig.switches.dwebPdpCustomVideo || false,
    relatedVideos: selectProductRelatedVideo(state),
    isPro: auth.user.isPro || false,
    isRedirectEligible: remoteConfig.switches?.isSlugRedirectEnabled,
    redirectBlockList: remoteConfig.configs?.pdRedirectTargetBlockList,
    priceRevealEnabled: remoteConfig.configs?.pdPriceReveal?.isActive,
    isSearchable: productPage.product?.isSearchable,
    redirectionParamBlackList: remoteConfig.configs?.redirectionParamBlackList,
    priveBand: auth.user?.priveBand || null,
    isDifferentialPricingEnabled: isEligibleForDifferentialPricing(state),
    nykaaNowRaapEnabled: getEligibleForVariant(
      state,
      AB_NYKAA_NOW_RAAP,
      'A'
    ),
    pinCode: auth.user.pinCode,
  };
};

const mapDispatchToProps = {
  fetchProduct: _fetchProduct,
  fetchBanners: _fetchBanners,
  trackJourney: trackProductJourney,
  productUnMount,
  generateBreadcrumbSchema: _generateBreadcrumbSchema,
  fetchBreadCrumbs: _fetchBreadCrumbs,
  generateProductSchema: _generateProductSchema,
};

const isIDChanged = (id?: string, idInStore?: string) => {
  if (id === idInStore) {
    return false;
  }

  return true;
};

const fetchData = async ({
  match,
  location,
  parentId,
  fetchProduct,
  trackJourney,
  staticContext,
  pdPageBanners,
  fetchBanners,
  loading,
  fetchingError,
  hasUniversalBanners,
  isPro,
  fetchBreadCrumbs,
  generateBreadcrumbSchema,
  generateProductSchema,
  isRedirectEligible,
  priceRevealEnabled,
  redirectBlockList,
  redirectionParamBlackList,
  priveBand,
  isDifferentialPricingEnabled,
  nykaaNowRaapEnabled,
  pinCode,
}) => {
  const { id } = match.params;
  const queryParams = new URLSearchParams(location.search);
  const skuId = queryParams.get('skuId');
  const regionId = queryParams.get('region_id');
  if (isIDChanged(id, parentId) && !loading && !fetchingError) {
    trackJourney(location.search);
    const { storeId } = staticContext || {};
    const productDataPromise = fetchProduct({
      id,
      skuId,
      ...(storeId && { store: storeId }),
      ...(isPro && { pro: isPro }),
      ...(priceRevealEnabled && { enablePR: priceRevealEnabled }),
      priveBand,
      ...(isDifferentialPricingEnabled && {
        enableDiffPricing: isDifferentialPricingEnabled,
      }),
      ...(nykaaNowRaapEnabled && !pinCode && regionId && {
        regionId,
        internalSlug: QC_INTERNAL_SLUG,
      }),
    });
    const productData = await productDataPromise;
    const currentSku = getSelectedSku(productData);
    if (productData || currentSku) {
      const schemaData = getSchemaData({
        ...currentSku,
        topReviews: productData.latestReviews,
      });
      generateProductSchema(schemaData);
      const primaryCategories = productData?.primaryCategories || {};
      if (isValidObject(primaryCategories)) {
        const categoryId = getCatgoryLevelId(primaryCategories);
        if (categoryId) {
          const breadCrumbs = await fetchBreadCrumbs(categoryId);
          if (breadCrumbs?.length) {
            generateBreadcrumbSchema(breadCrumbs);
          }
        }
      }
    }

    const productPagePromises = [productDataPromise];

    if (pdPageBanners) {
      const productBannerPromise = fetchBanners({
        productId: hasUniversalBanners ? 'all' : id,
      });
      productPagePromises.push(productBannerPromise);
    }

    const { slug: originalSlug } = productData || {};
    if (
      productData &&
      isRedirectEligible &&
      !originalSlug?.includes(SNS_PD_PAGE_PREFIX)
    ) {
      redirectIncorrectSlug({
        source: location.pathname,
        target: originalSlug,
        context: staticContext,
        queryString: location?.search || '',
        blockList: redirectBlockList,
        splitOn: '/p/',
        queryBlackList: redirectionParamBlackList,
      });
    }

    return Promise.all(productPagePromises);
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(frontloadConnect(fetchData, { onUpdate: true })(View));
